/* eslint-disable react/no-unescaped-entities */
/**
 * /join/:code — referral landing.
 *
 * Simple: capture the code in localStorage so signup can pick it up,
 * show a warm one-line welcome, and route to the login/register
 * screens. Zero extra data required, works even when logged out.
 */
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Sparkles } from "lucide-react";
import SoulumoWord from "../components/SoulumoWord";

const TOK = {
  ink: "#070d0b",
  cream: "#f5ecd9",
  creamDim: "rgba(245,236,217,0.72)",
  gold: "#d7bb7a",
  goldMist: "rgba(215,187,122,0.28)",
  goldGlow: "rgba(215,187,122,0.16)",
};

export default function JoinReferralPage() {
  const { code } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (code) {
      try {
        // Uses the same localStorage key as ReferralContext so the
        // signup flow picks it up automatically.
        localStorage.setItem("reclaim_referral_code", code.toUpperCase());
      } catch { /* private mode etc. */ }
    }
  }, [code]);

  // If already logged in, drop them at the home screen — the code
  // still sits in localStorage for the next signup to pick up.
  useEffect(() => {
    const t = localStorage.getItem("reclaim_token");
    if (t) {
      const timer = setTimeout(() => navigate("/", { replace: true }), 1200);
      return () => clearTimeout(timer);
    }
  }, [navigate]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: TOK.ink }}
      data-testid="join-referral-page"
    >
      <div
        className="w-full max-w-md p-8 rounded-3xl text-center"
        style={{
          background: "linear-gradient(180deg, #101816 0%, #0a1210 100%)",
          border: `1px solid ${TOK.goldMist}`,
        }}
      >
        <div
          className="w-14 h-14 rounded-full mx-auto flex items-center justify-center"
          style={{ background: TOK.goldGlow, border: `1px solid ${TOK.goldMist}` }}
        >
          <Sparkles size={22} style={{ color: TOK.gold }} />
        </div>
        <div
          className="mt-4 text-[0.68rem] uppercase tracking-[0.28em]"
          style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}
        >
          You've been invited
        </div>
        <h1
          className="text-3xl mt-2 flex items-baseline justify-center gap-3 flex-wrap"
          style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream }}
        >
          <span>Welcome to</span>
          <SoulumoWord size="md" />
        </h1>
        <p
          className="italic mt-3"
          style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.creamDim, fontSize: "1.02rem" }}
        >
          Your friend built themselves a personal audio journey and thought you'd love it too.
        </p>

        <div
          className="mt-6 inline-block px-3 py-1 rounded-full text-[0.66rem] uppercase tracking-[0.28em]"
          style={{
            background: TOK.goldGlow, border: `1px solid ${TOK.goldMist}`,
            color: TOK.gold, fontFamily: '"Manrope", sans-serif',
          }}
        >
          Invite code · {(code || "").toUpperCase()}
        </div>

        <div className="mt-8 flex flex-col gap-3">
          <button
            onClick={() => navigate("/register")}
            data-testid="join-register-btn"
            className="w-full py-3 rounded-full transition-all"
            style={{
              background: TOK.gold, color: TOK.ink,
              fontFamily: '"Manrope", sans-serif', fontSize: "0.78rem",
              letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 600,
            }}
          >
            Create my account
          </button>
          <button
            onClick={() => navigate("/login")}
            data-testid="join-login-btn"
            className="w-full py-3 rounded-full transition-all"
            style={{
              background: "transparent", color: TOK.cream,
              border: `1px solid ${TOK.goldMist}`,
              fontFamily: '"Manrope", sans-serif', fontSize: "0.78rem",
              letterSpacing: "0.24em", textTransform: "uppercase",
            }}
          >
            I already have one · Sign in
          </button>
        </div>
      </div>
    </div>
  );
}
